import { Box, Card, Chip, Divider, Grid, Link, Tooltip, Typography } from "@mui/material";
import { useListController, ListContextProvider, useListContext } from "ra-core";
import { fromUnixTime } from "date-fns";
import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import Scrollbar from "@/components/Scrollbar";
import { Content } from "@/components/content/Content";
import { FunctionField, TextField } from "@/components/field";
import { Datagrid } from "@/components/list/datagrid";
import { OdResourcePageProps } from "@/types/resource";
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import DateField2 from "@/components/field/DateField2";
import { OfflineFileResponseDto } from "@/gen/api/securefile";
import { RecPatch } from "@/dataProvider/tools";
import { genUrlWithHashid } from "@/utils/hashid";
import { useUserTimeFormat } from '@/utils/userTimeFormat';
import { OfflineFileTableSearchProvider } from "@/components/securefiles/OfflineFileTableSearchProvider";
import OfflineFileSearchRegion from "@/components/securefiles/OfflineFileTableSearchRegion";


const OfflineFileSearch: FC<OdResourcePageProps> = (props) => {

  const listCtrl = useListController({ basePath: '', filter: {}, filterDefaultValues: { completed: false } })
  
  return (
    <ListContextProvider value={listCtrl}>
      <OfflineFileSearchBody {...props} />
    </ListContextProvider>
  )
}


const OfflineFileSearchBody: FC<OdResourcePageProps> = ({
  options, resource,
}) => {
  const listCtx = useListContext()
  
  return (
    <Content pageName="Offline Files" resourceName="Offline Files" breadcrumbs={options?.breadcrumbs}>
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Card>
            <OfflineFileTableSearchProvider>
              <OfflineFileSearchRegion />
            </OfflineFileTableSearchProvider>
            <Divider />
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  {...listCtx}
                >
                  <FunctionField label="File Name" source="fileName" sortable={false} 
                    render={(d?: OfflineFileResponseDto&RecPatch) =>
                      <Typography variant="body2" sx={{maxWidth:250,display:'inline-block',wordWrap:'break-word'}}>
                        <Link underline="hover" component={RouterLink}
                          to={genUrlWithHashid(`/secure-files/$0/show`, d?.fileId ?? 0, d?.fileName)}
                        >
                          {d?.fileName}
                        </Link>
                      </Typography>
                    }
                  />
                  <FunctionField source="username" sortable={false} 
                    label={<>
                        Request User <Typography variant="caption">/</Typography>
                        <Typography variant="caption" component="p">Machine</Typography></>} 
                    render={(d?: OfflineFileResponseDto&RecPatch) => {
                      if (!d) { return null}
                      const fullname = d.firstName || d.lastName ? `${d.firstName} ${d.lastName}` : null
                      return (
                        <>
                          {fullname
                            ? <Tooltip title={d.username ?? ''}><Box>
                                <Typography variant="body2">{fullname}</Typography>
                              </Box></Tooltip>
                            : <Typography variant="body2">{d.username}</Typography>}
                          <Typography variant="caption" color="InactiveCaptionText" component="p" sx={{mt:1}}>{d.machine}</Typography>
                        </>
                      )
                    }}
                  />
                  <FunctionField label="Location" source="path" sortable={false}
                    render={(d?:OfflineFileResponseDto&RecPatch) => 
                      <Typography variant="caption" color="secondary" sx={{maxWidth:320,display:'inline-block',wordWrap:'break-word'}}>
                        <code>{d?.path}</code>
                      </Typography>
                    }
                  />
                  <FunctionField label="Access Info" source="accessBeginTime" sortable={false} 
                    render={(d?:OfflineFileResponseDto&RecPatch) => <OfflineFileAccessInfo d={d} />}
                  />
                  <DateField2 label="Requested Date" source="createdTime" sortable={false} />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default OfflineFileSearch;


const OfflineFileAccessInfo: FC<{d?:OfflineFileResponseDto}> = ({ d }) => {
  const format = useUserTimeFormat()
  if (!d) { return null }
  const t1 = fromUnixTime(d.accessBeginTime!)
  const t2 = fromUnixTime(d.accessEndTime!)
  const days = (d.accessEndTime! - d.accessBeginTime!) / 3600 / 24
  return (
    <Box sx={{ width: 150 }}>
      <Typography variant="caption" component="code"><Typography variant="caption" color="InactiveCaptionText">Start:</Typography> {format(t1, 'P')} | {days} {days > 1 ? "days" : "day"}</Typography>
      <Typography variant="caption" component="p"><Typography variant="caption" color="InactiveCaptionText">Access count:</Typography> {d.accessCount} of {d.accessCountMax}</Typography>
      {d?.isDeleted && <Chip sx={{ml: 4}} label="Completed" size="small" color="error" variant="outlined"/>}
    </Box>
  )
}




